// Drafting a behaviour ARTIFACT from a judged verdict: the model writes real
// Python plus a contract manifest, with tool access to stress-test both
// against the evidence before anything persists.
//
// Three postures, one per phase: generation is free (the drafting agent runs
// python over sampled windows and iterates), the artifact is free (its emit()
// is arbitrary sandboxed code), and the emission is bound (the manifest is
// validated here and every future run's rows pass the same boundary). This
// module owns the request and the acceptance check; the transport lives in
// xrayFactoryRun, like the judge's.

import { kebabId } from "./xrayRuntimeLensDraft.js";
import { skeletonizeShell } from "../../xray-factory/src/mine/skeleton.js";
import { validateBehaviourManifest } from "../../xray-factory/src/emit/contract.js";

export const ARTIFACT_SCHEMA = {
  type: "object",
  required: ["source", "manifest"],
  properties: {
    source: {
      type: "string",
      description:
        "Python source defining `emit(window)`. Stdlib only (regex, json, collections are all available); "
        + "no imports of numpy/pandas — the artifact runs on every scan and must stay cheap.",
    },
    manifest: {
      type: "object",
      required: ["name", "consumer", "decision", "falsifier", "substrate", "claims", "emits"],
      properties: {
        name: { type: "string", description: "What an operator calls this behaviour, 2-5 words." },
        consumer: { type: "string", description: "Who reads this behaviour's occurrences." },
        decision: { type: "string", description: "What that person decides differently." },
        falsifier: { type: "string", description: "The observation that would show this behaviour wrongly named." },
        substrate: {
          type: "object",
          properties: {
            tools: { type: "array", items: { type: "string" }, description: "Tool names this behaviour samples." },
            shell: { type: "boolean", description: "Whether shell-carrying tools are sampled." },
          },
        },
        claims: {
          type: "array",
          description: "The named probes emit() verdicts on — every occurrence's `claims` keys must come from here.",
          items: {
            type: "object",
            required: ["name"],
            properties: { name: { type: "string" }, proves: { type: "string" } },
          },
        },
        emits: {
          type: "object",
          description:
            "Field schema: name → {role}. Roles: target (the entity acted on; must state durable: true|false"
            + " and may state kind), metric (number), evidence (verbatim span), binding (any other named value).",
          additionalProperties: {
            type: "object",
            required: ["role"],
            properties: {
              role: { type: "string", enum: ["target", "metric", "evidence", "binding"] },
              kind: { type: "string" },
              durable: { type: "boolean" },
            },
          },
        },
      },
    },
    notes: { type: "string", description: "One sentence on anything a reader should distrust." },
  },
};

export const ARTIFACT_INSTRUCTIONS = `You are writing a behaviour ARTIFACT: Python that reads an agent trace's turn
windows and emits typed occurrences of ONE behaviour a judge already named.

## The window shape

Your \`emit(window)\` is called once per turn window:

  window = {
    "promptId": str|None, "sessionId": str, "endpointId": str|None, "ts": str|None,
    "calls": [
      { "key": str,            # tool_use block id — REQUIRED on every occurrence you emit
        "tool": str,           # tool name (e.g. "Bash")
        "command": str|None,   # shell command text when the tool carries one
        "skeleton": str|None,  # host-computed normalized shape — door on this, never re-derive matching
        "input": dict,         # the tool call's arguments ({"_truncated": True, "_preview": ...} when oversized)
        "output": str|None,    # what came back (head+tail capped), None when unrecorded
        "call": str },         # answered | errored | unrecorded — host knowledge, do not re-emit
      ...
    ]
  }

## What you return

A list of occurrence dicts (empty list when the window holds none of this
behaviour):

  { "key": <the call's key you are claiming>,
    "values": { <field>: <scalar> },      # every field must be declared in your manifest's emits
    "claims": { <claim name>: bool } }    # every name must be declared in your manifest's claims

The host stamps identity (behaviour, version), transport state (call), and
session metadata. Do not emit them. An occurrence keyed to a block id that is
not in the window is REFUSED — claim real calls or nothing.

## The verdict is a CONTRACT, not a suggestion

The prompt carries the judged contract: the door (observed shapes, or an
outcome pattern), the narrowing \`where\` when one was claimed, the named
claims with their probes, and the fields the judge wants. Implement it
exactly:

  - Door on \`call["skeleton"]\` membership for shape doors (the host computes
    it with the same normalizer the miner clustered with — never re-derive
    matching from the raw command), or on the output pattern for
    outcome-defined behaviours.
  - Your manifest MUST declare exactly the contract's claim names, and every
    occurrence verdicts on each of them.
  - Then go FURTHER where the evidence supports it: python recovers what
    regexes cannot — parse the output, compute fields, cross-reference calls
    within the window. That surplus is the reason you exist; the contract is
    the floor, never the ceiling.

## Rules, each from a real failure

1. CLAIM ONLY WHAT THE WINDOW SHOWS. A run whose output was never recorded
   cannot support a claim about its result — verdict it false or omit the
   occurrence, never guess. The judge's falsifier is your spec: emit the
   verdicts that would let a reader see it fire.
2. TARGETS ARE COORDINATES. A field with role target should hold the most
   identifiable form of the entity you can recover (a path, a URL), and your
   manifest must state whether it is durable — merging session-scratch names
   across traces manufactures false joins.
3. DETERMINISM. Same windows in, same occurrences out: no clock, no random,
   no network. The artifact re-runs over history and its output must be
   attributable and reproducible.
4. STAY CHEAP. This runs over every scanned window. Prefer string operations
   and compiled regex; no numpy/pandas.
5. STRESS-TEST BEFORE RETURNING. You have a python tool and sample windows.
   Run your emit() against them, read what comes back, and fix what lies —
   an occurrence that misclaims in front of you now will misclaim on every
   trace forever. Only return source you have executed.`;

/**
 * The drafting task payload for one judged behaviour.
 *
 * Windows ride in the prompt so the agent can paste them into its python tool
 * (args.data) and iterate. Capped: the agent needs enough evidence to falsify
 * itself, not the whole corpus.
 */
export function buildArtifactDraftRequest(verdict, cluster, windows, { maxWindows = 6 } = {}) {
  const sample = (windows || []).slice(0, maxWindows);
  // The door the miner observed: every distinct core in the kept instances,
  // not just the merge survivor — the same set the declarative draft doors on,
  // so the two implementations of one verdict can never disagree about what
  // they claim.
  const observed = new Set(cluster?.skeleton ? [cluster.skeleton] : []);
  for (const inst of cluster?.instances || []) {
    const core = skeletonizeShell(String(inst?.command || "")).core;
    if (core) observed.add(core);
    if (observed.size >= 12) break;
  }
  const door = verdict?.outcomePattern
    ? `Door — OUTCOME-DEFINED: claim any shell call whose OUTPUT matches /${verdict.outcomePattern}/ (a call with no captured output is never claimed). Do not door on a shape.`
    : `Door — the observed shapes (test \`call["skeleton"]\` for membership):\n${[...observed].map((sk) => `  - ${sk}`).join("\n")}`;
  const checks = Array.isArray(verdict?.checks) ? verdict.checks : [];
  return {
    instructions: ARTIFACT_INSTRUCTIONS,
    schema: ARTIFACT_SCHEMA,
    prompt:
      `Behaviour (already judged): ${verdict?.name}\n`
      + `Summary: ${verdict?.summary}\n`
      + `Reader: ${verdict?.consumer}\nDecides: ${verdict?.decision}\n`
      + `Falsifier: ${verdict?.falsifier}\n\n`
      + "## The contract to implement\n"
      + `${door}\n`
      + (verdict?.where ? `Narrowing where (over the raw command): /${verdict.where}/\n` : "")
      + (checks.length
        ? `Claims — your manifest declares EXACTLY these names, and every occurrence verdicts on each:\n${checks.map((c) => `  - "${c.name}" — on ${c.on}: /${c.pattern}/`).join("\n")}\n`
        : "")
      + (Array.isArray(verdict?.fields) && verdict.fields.length
        ? `Fields the judge wants (bind what the evidence supports): ${verdict.fields.join(", ")}\n`
        : "")
      + `\nSample windows (${sample.length} of ${windows?.length || 0} scanned — stress-test against these):\n`
      + JSON.stringify(sample, null, 1),
  };
}

/**
 * Accept or refuse what the drafting agent returned.
 *
 * The behaviour id derives from the verdict's name here — the agent does not
 * pick its own identity — and the manifest is validated with the store's own
 * identity fields injected, so acceptance and persistence cannot disagree.
 *
 * @returns {{ok, problems: string[], artifact: object|null}}
 */
export function validateArtifactDraft(response, verdict) {
  const problems = [];
  const source = String(response?.source || "");
  if (!source.trim()) problems.push("source: the agent returned no implementation");
  if (!/def\s+emit\s*\(/.test(source)) problems.push("source: must define `emit(window)`");
  if (/\b(?:import\s+(?:numpy|pandas|scipy)|from\s+(?:numpy|pandas|scipy))/.test(source)) {
    problems.push("source: numpy/pandas/scipy are not available to artifacts — stdlib only");
  }

  const behaviour = kebabId(verdict?.name, "behaviour");
  const artifact = {
    behaviour,
    source,
    manifest: response?.manifest && typeof response.manifest === "object" ? response.manifest : {},
    draftedFrom: {
      verdictKey: verdict?.key || null,
      falsifier: verdict?.falsifier || null,
      claimScope: verdict?.claimScope || null,
    },
  };
  const check = validateBehaviourManifest({
    ...artifact.manifest,
    behaviour,
    version: "fnv:pending",
  });
  problems.push(...check.problems);

  // Contract continuity: the judge's claim names are the reviewable surface,
  // and an implementation that renames or drops one has quietly narrowed what
  // the verdict promised. Extra claims are welcome — the surplus is the point.
  const declared = new Set((artifact.manifest?.claims || []).map((c) => String(c?.name || "").trim()));
  for (const chk of Array.isArray(verdict?.checks) ? verdict.checks : []) {
    const name = String(chk?.name || "").trim();
    if (name && !declared.has(name)) {
      problems.push(`manifest.claims: missing "${name}" — the judged contract's claims are fixed; implement them, then add more if the evidence supports it`);
    }
  }

  return { ok: problems.length === 0, problems, artifact: problems.length === 0 ? artifact : null };
}
